<div class="profile-layout">
  <a routerLink="/dashboard" class="back-link">
    <i class="bi bi-arrow-left"></i> Back to Dashboard
  </a>

  <h1 class="page-title">{{ completionMode ? 'Complete Your Profile' : 'My Profile' }}</h1>
  <p class="page-subtitle">
    {{ completionMode ? 'Add the required details before starting trial or course access.' : 'Manage your account information, password, and preferences.' }}
  </p>

  <!-- Sign out — mobile entry point. Logout is hidden from the header on phones
       (the header is too small for it) so we surface it here. CSS hides this
       block on desktop web where the header Logout link is still visible. -->
  <div class="profile-signout-card panel">
    <div class="profile-signout-info">
      <h2 class="profile-signout-title"><i class="bi bi-box-arrow-right"></i> Sign out</h2>
      <p class="profile-signout-text">End your session on this device. You'll need to sign in again to use {{ brand.productName }}.</p>
    </div>
    <a routerLink="/logout" class="btn btn-outline-danger profile-signout-btn">
      <i class="bi bi-box-arrow-right"></i> Sign out
    </a>
  </div>

  <div class="panel profile-panel">
    <!-- Tabs -->
    <ul class="profile-tabs" role="tablist">
      <li role="presentation">
        <button class="profile-tab" [class.active]="activeTab === 'profile'" (click)="selectTab('profile')" type="button">
          My Profile
        </button>
      </li>
      <li role="presentation">
        <button class="profile-tab" [class.active]="activeTab === 'password'" (click)="selectTab('password')" type="button">
          Change Password
        </button>
      </li>
      <li role="presentation">
        <button class="profile-tab" [class.active]="activeTab === 'data'" (click)="selectTab('data')" type="button">
          Your Data
        </button>
      </li>
      <li role="presentation">
        <button class="profile-tab" [class.active]="activeTab === 'delete'" (click)="selectTab('delete')" type="button">
          Delete Account
        </button>
      </li>
    </ul>

    <!-- Loading indicator -->
    <div *ngIf="isLoading" class="panel-loading">
      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
      Loading...
    </div>

    <div *ngIf="!isLoading && hasPendingDeletion()" class="alert alert-warning mb-4">
      <i class="bi bi-exclamation-triangle"></i>
      Your account is scheduled for deletion on {{ formatDate(userProfile?.deletionScheduledAt || '') }}.
      You can cancel this before the scheduled date.
      <button type="button" class="btn btn-sm btn-outline-dark ms-2" (click)="cancelAccountDeletion()" [disabled]="isCancellingDeletion">
        {{ isCancellingDeletion ? 'Cancelling...' : 'Cancel deletion' }}
      </button>
    </div>

    <!-- Tab 1: My Profile -->
    <div *ngIf="!isLoading && activeTab === 'profile'" class="tab-content">
      <div class="alert alert-info mb-4">
        <i class="bi bi-info-circle"></i>
        {{ completionMode ? 'These details are required before trial or paid course access is activated.' : 'Update your profile information. Fields marked as read-only cannot be changed.' }}
      </div>

      <form [formGroup]="profileForm" (ngSubmit)="saveProfile()">
        <div class="row mb-3">
          <div class="col-md-6">
            <label for="username" class="form-label">Username</label>
            <input type="text" class="form-control" id="username" formControlName="username" readonly>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="firstName" class="form-label">First Name <span class="text-danger" *ngIf="requiresProfileFields()">*</span></label>
            <input type="text" class="form-control" id="firstName" formControlName="firstName"
                   [class.is-invalid]="profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched">
            <div class="invalid-feedback" *ngIf="profileForm.get('firstName')?.hasError('required') && profileForm.get('firstName')?.touched">
              First name is required.
            </div>
          </div>
          <div class="col-md-6">
            <label for="lastName" class="form-label">Last Name <span class="text-danger" *ngIf="requiresProfileFields()">*</span></label>
            <input type="text" class="form-control" id="lastName" formControlName="lastName"
                   [class.is-invalid]="profileForm.get('lastName')?.invalid && profileForm.get('lastName')?.touched">
            <div class="invalid-feedback" *ngIf="profileForm.get('lastName')?.hasError('required') && profileForm.get('lastName')?.touched">
              Last name is required.
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="email" class="form-label">Email <span class="text-danger">*</span></label>
            <input type="email" class="form-control" id="email" formControlName="email"
                   [class.is-invalid]="profileForm.get('email')?.invalid && profileForm.get('email')?.touched">
            <div class="invalid-feedback" *ngIf="profileForm.get('email')?.hasError('required') && profileForm.get('email')?.touched">
              Email is required.
            </div>
            <div class="invalid-feedback" *ngIf="profileForm.get('email')?.hasError('email') && profileForm.get('email')?.touched">
              Please enter a valid email address.
            </div>
          </div>
          <div class="col-md-6">
            <label for="countryId" class="form-label">Country <span class="text-danger" *ngIf="requiresProfileFields()">*</span></label>
            <select class="form-select" id="countryId" formControlName="countryId" *ngIf="requiresProfileFields()"
                    [class.is-invalid]="profileForm.get('countryId')?.invalid && profileForm.get('countryId')?.touched">
              <option [ngValue]="null">Select a country...</option>
              <option *ngFor="let country of countries" [ngValue]="country.id">
                {{ country.name }} ({{ country.code }})
              </option>
            </select>
            <div class="invalid-feedback d-block" *ngIf="profileForm.get('countryId')?.hasError('required') && profileForm.get('countryId')?.touched">
              Country is required.
            </div>
            <input type="text" class="form-control" id="country" formControlName="country" *ngIf="!requiresProfileFields()" readonly>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <label for="phoneCountryPrefix" class="form-label">Phone Country Prefix</label>
            <select class="form-select" id="phoneCountryPrefix" formControlName="phoneCountryPrefix">
              <option value="">Select...</option>
              <option *ngFor="let country of phoneCountries" [value]="country.prefix">
                {{ country.flag }} {{ country.name }} ({{ country.prefix }})
              </option>
            </select>
          </div>
          <div class="col-md-8">
            <label for="phoneNumber" class="form-label">Phone Number</label>
            <input type="text" class="form-control" id="phoneNumber" formControlName="phoneNumber"
                   placeholder="Enter phone number" maxlength="20">
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="authorityId" class="form-label">Authority <span class="text-danger" *ngIf="requiresProfileFields()">*</span></label>
            <select class="form-select" id="authorityId" formControlName="authorityId" *ngIf="requiresProfileFields()"
                    [disabled]="!!profileForm.get('authorityId')?.disabled"
                    [class.is-invalid]="profileForm.get('authorityId')?.invalid && profileForm.get('authorityId')?.touched">
              <option [ngValue]="null">Select an authority...</option>
              <option *ngFor="let authority of filteredAuthorities" [ngValue]="authority.id">
                {{ authority.countryName }}: {{ authority.name }}
              </option>
            </select>
            <div class="invalid-feedback d-block" *ngIf="profileForm.get('authorityId')?.hasError('required') && profileForm.get('authorityId')?.touched">
              Authority is required.
            </div>
            <input type="text" class="form-control" id="authority" formControlName="authority" *ngIf="!requiresProfileFields()" readonly>
          </div>
          <div class="col-md-6">
            <label class="form-label">Account Created</label>
            <div class="form-control-plaintext">{{ formatDate(profileForm.get('createdAt')?.value) }}</div>
          </div>
        </div>

        <div class="mb-3">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="acceptMarketing" formControlName="acceptMarketing">
            <label class="form-check-label" for="acceptMarketing">
              I accept marketing communications
            </label>
          </div>
        </div>

        <div class="form-actions">
          <div *ngIf="profileMessage" class="form-message">
            <div class="alert mb-0" [ngClass]="{'alert-success': profileMessage.type === 'success', 'alert-danger': profileMessage.type === 'error'}" role="alert">
              <i class="bi" [ngClass]="{'bi-check-circle': profileMessage.type === 'success', 'bi-exclamation-circle': profileMessage.type === 'error'}"></i>
              {{ profileMessage.text }}
            </div>
          </div>
          <button type="submit" class="btn btn-primary" [disabled]="isSaving || profileForm.invalid">
            <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isSaving ? 'Saving...' : (completionMode ? 'Save and Continue' : 'Save Changes') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Tab 2: Change Password -->
    <div *ngIf="!isLoading && activeTab === 'password'" class="tab-content">
      <div class="alert alert-info mb-4" *ngIf="!isExternalUser">
        <i class="bi bi-info-circle"></i> Change your account password. Make sure to use a strong password for better security.
      </div>
      <div class="alert alert-warning mb-4" *ngIf="isExternalUser">
        <i class="bi bi-exclamation-triangle"></i> <strong>Password change is not available for external accounts.</strong> Your account is managed by an external provider.
      </div>

      <form [formGroup]="passwordForm" (ngSubmit)="changePassword()">
        <div class="mb-3">
          <label for="currentPassword" class="form-label">Current Password <span class="text-danger">*</span></label>
          <input type="password" class="form-control" id="currentPassword" formControlName="currentPassword" maxlength="100"
                 [disabled]="isExternalUser"
                 [class.is-invalid]="(passwordForm.get('currentPassword')?.invalid && passwordForm.get('currentPassword')?.touched) || passwordError">
          <div class="invalid-feedback" *ngIf="passwordForm.get('currentPassword')?.invalid && passwordForm.get('currentPassword')?.touched && !passwordError">
            Current password is required.
          </div>
          <div class="invalid-feedback" *ngIf="passwordError">
            {{ passwordError }}
          </div>
        </div>

        <div class="mb-3">
          <label for="newPassword" class="form-label">New Password <span class="text-danger">*</span></label>
          <input type="password" class="form-control" id="newPassword" formControlName="newPassword" maxlength="100"
                 [disabled]="isExternalUser"
                 [class.is-invalid]="passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched">
          <div class="invalid-feedback" *ngIf="passwordForm.get('newPassword')?.hasError('required') && passwordForm.get('newPassword')?.touched">
            New password is required.
          </div>
          <div class="invalid-feedback" *ngIf="passwordForm.get('newPassword')?.hasError('minlength') && passwordForm.get('newPassword')?.touched">
            Password must be at least 8 characters long.
          </div>
          <div class="invalid-feedback" *ngIf="passwordForm.get('newPassword')?.hasError('maxlength') && passwordForm.get('newPassword')?.touched">
            Password must be no more than 100 characters long.
          </div>

          <!-- Password Strength Indicator -->
          <div *ngIf="passwordForm.get('newPassword')?.value" class="mt-2">
            <div class="progress" style="height: 8px;">
              <div class="progress-bar"
                   [style.width.%]="passwordStrength"
                   [style.background-color]="passwordStrengthColor"
                   role="progressbar">
              </div>
            </div>
            <small [style.color]="passwordStrengthColor" class="mt-1 d-block">
              Password Strength: {{ passwordStrengthLabel }}
            </small>
          </div>
        </div>

        <div class="mb-3">
          <label for="confirmPassword" class="form-label">Confirm New Password <span class="text-danger">*</span></label>
          <input type="password" class="form-control" id="confirmPassword" formControlName="confirmPassword" maxlength="100"
                 [disabled]="isExternalUser"
                 [class.is-invalid]="(passwordForm.get('confirmPassword')?.invalid || passwordForm.errors?.['passwordMismatch']) && passwordForm.get('confirmPassword')?.touched">
          <div class="invalid-feedback" *ngIf="passwordForm.get('confirmPassword')?.hasError('required') && passwordForm.get('confirmPassword')?.touched">
            Please confirm your new password.
          </div>
          <div class="invalid-feedback" *ngIf="passwordForm.get('confirmPassword')?.hasError('maxlength') && passwordForm.get('confirmPassword')?.touched">
            Password must be no more than 100 characters long.
          </div>
          <div class="invalid-feedback" *ngIf="passwordForm.errors?.['passwordMismatch'] && passwordForm.get('confirmPassword')?.touched">
            Passwords do not match.
          </div>
        </div>

        <div class="form-actions">
          <div *ngIf="passwordMessage" class="form-message">
            <div class="alert mb-0" [ngClass]="{'alert-success': passwordMessage.type === 'success', 'alert-danger': passwordMessage.type === 'error'}" role="alert">
              <i class="bi" [ngClass]="{'bi-check-circle': passwordMessage.type === 'success', 'bi-exclamation-circle': passwordMessage.type === 'error'}"></i>
              {{ passwordMessage.text }}
            </div>
          </div>
          <button type="submit" class="btn btn-primary" [disabled]="isChangingPassword || passwordForm.invalid || isExternalUser">
            <span *ngIf="isChangingPassword" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isChangingPassword ? 'Updating...' : 'Update Password' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Tab 3: Your Data -->
    <div *ngIf="!isLoading && activeTab === 'data'" class="tab-content">
      <h2 class="h5">Your data</h2>
      <p>Download a ZIP archive containing a portable JSON copy of your account, study data, consent history, support messages, and privacy requests.</p>
      <div *ngIf="exportMessage" class="alert" [ngClass]="{'alert-success': exportMessage.type === 'success', 'alert-danger': exportMessage.type === 'error'}">
        {{ exportMessage.text }}
      </div>
      <button type="button" class="btn btn-outline-primary" (click)="requestDataExport()" [disabled]="isRequestingExport">
        {{ isRequestingExport ? 'Preparing export...' : 'Download data export' }}
      </button>
    </div>

    <!-- Tab 4: Delete Account -->
    <div *ngIf="!isLoading && activeTab === 'delete'" class="tab-content">
      <div class="alert alert-warning mb-4">
        <i class="bi bi-exclamation-triangle"></i>
        <strong>Warning:</strong> Your account will be scheduled for deletion. We keep the data for the configured retention period so the request can be cancelled if you come back before the scheduled deletion date.
      </div>
      <div class="alert alert-info mb-4" *ngIf="hasPendingDeletion()">
        Your account is already scheduled for deletion on {{ formatDate(userProfile?.deletionScheduledAt || '') }}.
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-danger" (click)="showDeleteConfirmDialog1()" [disabled]="isDeleting || hasPendingDeletion()">
          Schedule Account Deletion
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Dialog 1 -->
<div *ngIf="showDeleteConfirm1" class="modal-overlay" (click)="closeDeleteConfirm1()">
  <div class="modal-dialog" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Delete Account?</h5>
        <button type="button" class="btn-close" (click)="closeDeleteConfirm1()" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to schedule your account for deletion?</p>
        <p class="text-danger"><strong>You will be logged out immediately. You can sign in again before the scheduled deletion date to cancel the request.</strong></p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDeleteConfirm1()">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="proceedToDeleteConfirm2()">Continue</button>
      </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Dialog 2 -->
<div *ngIf="showDeleteConfirm2" class="modal-overlay" (click)="closeDeleteConfirm2()">
  <div class="modal-dialog" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Final Confirmation</h5>
        <button type="button" class="btn-close" (click)="closeDeleteConfirm2()" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p class="text-danger"><strong>This is your final warning!</strong></p>
        <p>Your account will be scheduled for deletion after the configured retention period. After that date, account access will be blocked and the data can be purged.</p>
        <p>Are you absolutely sure you want to proceed?</p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDeleteConfirm2()">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="deleteAccount()" [disabled]="isDeleting">
          <span *ngIf="isDeleting" class="spinner-border spinner-border-sm me-2" role="status"></span>
          {{ isDeleting ? 'Scheduling...' : 'Yes, Schedule Deletion' }}
        </button>
      </div>
    </div>
  </div>
</div>
